import { Flex } from "antd";
import type { FC, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
    AssortmentTaskType,
    SubtaskAssortmentMatrix,
    SubtaskAssortmentMatrixPayload,
    SubtaskFacings,
    SubtaskFacingsPayload,
    SubtaskFinGroupPhotos,
    SubtaskFinGroupPhotosPayload,
    SubtaskProductsMatrix,
    SubtaskProductsMatrixPayload,
    TaskPhotosEntity,
    TaskStatus,
} from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { SubtaskCard } from "@/components/TasksModals/components/SubtaskCard";
import { PrimaryText } from "@/components/Typography/Styled";
import { ROUTES } from "@/constants";
import { useAppSelector } from "@/hooks/redux";

type CardItem = {
    key: string;
    title: string | ReactNode;
    subtask: TaskPhotosEntity;
    extra?: ReactNode;
};

const getFacingsStatus = (amount: number = null, quantity: number = null) => {
    if (amount === null || quantity === null) return TaskStatus.Rejected;

    return amount >= quantity ? TaskStatus.Completed : TaskStatus.Rejected;
};

interface AssortmentMatrixSubtaskViewProps {
    subtask: SubtaskAssortmentMatrix;
    payload: SubtaskAssortmentMatrixPayload;
}

export const AssortmentMatrixSubtaskView: FC<
    AssortmentMatrixSubtaskViewProps
> = ({ subtask, payload }) => {
    const { products } = useAppSelector((s) => s.offline);

    let items: CardItem[] = [];

    switch (subtask.type) {
        case AssortmentTaskType.ProductsMatrix: {
            const s = subtask as SubtaskProductsMatrix;
            const p = payload as SubtaskProductsMatrixPayload;

            items = s.context.products.map((barcode) => {
                const currentProductName = products?.[barcode]?.name;

                return {
                    key: barcode,
                    title: currentProductName ? (
                        <Link
                            to={buildRoutePath(ROUTES.ProductPage, { barcode })}
                        >
                            <PrimaryText underline>
                                {currentProductName}
                            </PrimaryText>
                        </Link>
                    ) : (
                        barcode
                    ),
                    subtask: p.products[barcode],
                };
            });
            break;
        }

        case AssortmentTaskType.FinGroupPhotos: {
            const s = subtask as SubtaskFinGroupPhotos;
            const p = payload as SubtaskFinGroupPhotosPayload;

            items = s.context.photos.map((finGroup) => ({
                key: finGroup,
                title: finGroup,
                subtask: p.tasks[finGroup],
            }));
            break;
        }

        case AssortmentTaskType.Facings: {
            const s = subtask as SubtaskFacings;
            const p = payload as SubtaskFacingsPayload;

            items = s.context.facings.map(({ finGroup, quantity }) => {
                const item = p.facings[finGroup];
                const amount = item?.amount ?? null;

                return {
                    key: finGroup,
                    title: finGroup,
                    subtask: {
                        ...item,
                        status: getFacingsStatus(amount, quantity),
                    },
                    extra: (
                        <Flex justify="space-between">
                            <Flex gap={8}>
                                <PrimaryText>Введено:</PrimaryText>

                                <PrimaryText fontSize={16}>
                                    {amount ?? "—"}
                                </PrimaryText>
                            </Flex>

                            <Flex gap={8}>
                                <PrimaryText>План:</PrimaryText>

                                <PrimaryText fontSize={16}>
                                    {quantity ?? "?"}
                                </PrimaryText>
                            </Flex>
                        </Flex>
                    ),
                };
            });
            break;
        }

        default:
            items = [];
    }

    return (
        <>
            {items.map(({ key, subtask, title, extra }) => (
                <SubtaskCard key={key} subtask={subtask} title={title}>
                    {extra}
                </SubtaskCard>
            ))}
        </>
    );
};
